import AccessTimeIcon from "@mui/icons-material/AccessTime";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import type { NotificationFeedItem } from "@situate/contracts/runtime";
import { ConsoleSurface, SeverityAccent, getSeverityAccentColor } from "@/shared/ui";
import { formatNotificationCreatedAt } from "@/features/deployment-dashboard/notification-center/notification-stream-item-format";

interface NotificationListItemProps {
  notification: NotificationFeedItem;
  notificationCount?: number;
  selected?: boolean;
  onSelect?: (notification: NotificationFeedItem) => void;
}

/**
 * Single notification row using the notification-delivery projection.
 */
export function NotificationListItem({
  notification,
  notificationCount = 1,
  selected = false,
  onSelect,
}: NotificationListItemProps) {
  const theme = useTheme();
  const stripeColor = getSeverityAccentColor(theme, notification.severity);

  const content = (
    <ConsoleSurface
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        width: "100%",
        px: 2.5,
        py: 2,
        borderLeft: `4px solid ${stripeColor}`,
        backgroundColor: selected ? alpha(theme.palette.common.black, 0.18) : undefined,
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <Chip
            label={notification.type === "alert" ? "Alert" : "System"}
            size="small"
            sx={(theme) => {
              const color =
                notification.type === "alert"
                  ? notification.alert_status === "resolved"
                    ? {
                        main: theme.palette.text.secondary,
                        light: theme.palette.text.secondary,
                      }
                    : theme.palette.error
                  : {
                      main: "#9c6ade",
                      light: "#c7a8ef",
                    };
              return {
                height: 20,
                flexShrink: 0,
                fontSize: "0.625rem",
                fontWeight: 700,
                backgroundColor: alpha(color.main, 0.15),
                color: color.light,
                border: `1px solid ${alpha(color.main, 0.35)}`,
                "& .MuiChip-label": { px: 1 },
              };
            }}
          />
          <Typography variant="subtitle1" sx={{ fontWeight: 700, minWidth: 0 }}>
            {notification.title}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          <SeverityAccent
            level={notification.severity}
            label={notification.severity.toUpperCase()}
          />
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {notification.message}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <AccessTimeIcon
            aria-hidden="true"
            sx={{ fontSize: "0.875rem", color: "text.secondary" }}
          />
          <Typography variant="caption" color="text.secondary">
            {formatNotificationCreatedAt(notification.created_at)}
          </Typography>
        </Box>
      </Box>
      {notificationCount > 1 && (
        <Box sx={{ display: "flex", mt: 0.75 }}>
          <Chip
            label={`Grouped ${notificationCount} notifications`}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: "0.625rem" }}
          />
        </Box>
      )}
    </ConsoleSurface>
  );

  if (notification.type !== "alert" || onSelect === undefined) {
    return content;
  }

  return (
    <ButtonBase
      onClick={() => onSelect(notification)}
      aria-label={`View alert: ${notification.title}`}
      aria-pressed={selected}
      sx={(theme) => ({
        display: "block",
        width: "100%",
        textAlign: "left",
        borderRadius: 1,
        outline: selected ? `2px solid ${alpha(theme.palette.common.white, 0.2)}` : undefined,
        outlineOffset: 2,
      })}
    >
      {content}
    </ButtonBase>
  );
}
